/**
 * The standing forwarding rule: which days, which hours, between which dates.
 *
 * Its own file rather than more rows on `DivertCard`, and not because of length:
 * this is one self-contained decision with its own vocabulary (a week, a zone, a
 * pair of dates), while the card around it is about a switch and a number.
 *
 * The week itself is `HoursSchedule`, the same editor the working-hours tab
 * uses. Reused deliberately - two schedule editors in one product that behave
 * differently is a worse cost than the one difference in MEANING this rule has,
 * which is said in the note below rather than built into a second component: a
 * ticked day with an empty window is the whole day here.
 */
import { useI18n } from "../../../../i18n/I18nProvider";
import { DatePicker, TimezoneCombobox } from "@wa/ui";
import type { CallDivertSchedule } from "../../../../lib/callDivert";
import { Field, FieldGroup, Note, ToggleRow } from "../panel/fields";
import { HoursSchedule } from "./HoursSchedule";

export function DivertScheduleFields({
  value,
  onChange,
  /** A number is saved, so the rule has somewhere to send anything. */
  hasTarget,
}: {
  value: CallDivertSchedule;
  onChange: (next: CallDivertSchedule) => void;
  hasTarget: boolean;
}) {
  const { t, locale } = useI18n();
  const set = (patch: Partial<CallDivertSchedule>) => onChange({ ...value, ...patch });

  // Switched on with nothing ticked describes no moment at all. Cheap to check
  // and worth saying: the rule looks armed on the card and can never fire.
  const noDay = value.enabled && value.days.every((day) => !day.open);
  // A range that ended is not an error - a holiday that is over is a rule
  // somebody may want to keep for next year - but it does read as active when
  // it is not, so the card says which.
  const past = value.enabled && value.to !== "" && value.to < new Date().toISOString().slice(0, 10);

  return (
    <FieldGroup title={t("settings.forwarding.scheduleGroup")}>
      <Note tone="mut">{t("settings.forwarding.scheduleHint")}</Note>

      <ToggleRow
        label={t("settings.forwarding.scheduleEnable")}
        hint={t("settings.forwarding.scheduleEnableHint")}
        checked={value.enabled}
        onChange={(enabled) => set({ enabled })}
      />

      {value.enabled && !hasTarget ? (
        <Note tone="warn">{t("settings.forwarding.scheduleNeedsNumber")}</Note>
      ) : null}
      {noDay ? <Note tone="warn">{t("settings.forwarding.scheduleNoDay")}</Note> : null}
      {past ? <Note tone="mut">{t("settings.forwarding.schedulePast")}</Note> : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("settings.forwarding.scheduleFrom")} hint={t("settings.forwarding.scheduleFromHint")}>
          <DatePicker value={value.from} locale={locale} onChange={(from) => set({ from })} />
        </Field>
        <Field label={t("settings.forwarding.scheduleTo")} hint={t("settings.forwarding.scheduleToHint")}>
          <DatePicker value={value.to} locale={locale} onChange={(to) => set({ to })} />
        </Field>
      </div>

      <Field label={t("settings.forwarding.scheduleTimezone")} hint={t("settings.forwarding.scheduleTimezoneHint")}>
        <TimezoneCombobox
          className="h-11"
          value={value.timezone}
          onChange={(timezone) => set({ timezone })}
          aria-label={t("settings.forwarding.scheduleTimezone")}
          searchPlaceholder={t("common.timezoneSearch")}
          emptyLabel={t("common.timezoneNone")}
        />
      </Field>

      {/* The one place this week reads differently from the working one. Said
          here, next to the fields it applies to, rather than in the group's
          opening line where it would be read once and forgotten. */}
      <Note tone="info">{t("settings.forwarding.scheduleAllDay")}</Note>
      <HoursSchedule days={value.days} onChange={(days) => set({ days })} disabled={!value.enabled} />
    </FieldGroup>
  );
}
